import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { environment } from '../environments/environment';

// DTO matching the NestJS backend's InvoiceResponseDto
export interface InvoiceResponseDto {
  InvoiceId: string;
  // Sequential invoice/repair-order number (the human-facing reference).
  InvoiceNumber: number;
  CustUserId: string;
  InvoiceStatusId: string;
  StartDate?: Date;
  TimeEstimate?: string;
  WorkCompletionDate?: Date;
  InvoicePayDate?: Date;
  SubTotal: number;
  Tax: number;
  Total: number;
  CreatedAt: Date;
  UpdatedAt: Date;
}

export interface CreateInvoiceDto {
  CustUserId: string;
  InvoiceStatusId: string;
  StartDate?: string;
  TimeEstimate?: string;
  SubTotal?: number;
  Tax?: number;
  Total?: number;
}

export interface UpdateInvoiceDto {
  InvoiceStatusId?: string;
  StartDate?: string;
  TimeEstimate?: string;
  WorkCompletionDate?: string;
  InvoicePayDate?: string;
  SubTotal?: number;
  Tax?: number;
  Total?: number;
}

export interface UpdateWorkItemDto {
  WorkItemStatusId?: string;
  EmpUserId?: string | null;
  Title?: string;
  Miles?: number;
  ShopHours?: number;
  PartsPricing?: number;
  // null clears an existing flat-fee override.
  FlatFee?: number | null;
  FlatFeeTaxable?: boolean;
  StartDate?: string;
  EndDate?: string;
  Notes?: string;
  RejectionReason?: string | null;
}

// Board DTOs matching the NestJS backend's InvoiceBoardResponseDto
export interface InvoiceBoardWorkItemDto {
  WorkItemId: string;
  InvoiceVehicleId: string;
  WorkItemStatusId: string;
  WorkItemStatus: string;
  EmpUserId?: string;
  EmpFirstName?: string;
  EmpLastName?: string;
  EmpHourlyRate?: number;
  Title: string;
  Miles?: number;
  ShopHours?: number;
  PartsPricing?: number;
  FlatFee?: number | null;
  FlatFeeTaxable?: boolean;
  // Server-computed pricing (util/work-item-pricing.ts on the API).
  LaborAmount: number;
  LineTotal: number;
  Notes?: string;
  RejectionReason?: string;
}

export interface PricingConfigDto {
  taxRate: number;
}

export interface InvoiceBoardVehicleDto {
  InvoiceVehicleId: string;
  VehicleId: string;
  Year?: string;
  Make?: string;
  Model?: string;
  Vin?: string;
  WorkItems: InvoiceBoardWorkItemDto[];
}

export interface InvoiceBoardResponseDto {
  InvoiceId: string;
  // Sequential invoice/repair-order number (the human-facing reference).
  InvoiceNumber: number;
  CustUserId: string;
  CustFirstName: string;
  CustLastName: string;
  CustEmail: string;
  InvoiceStatusId: string;
  InvoiceStatus: string;
  TimeEstimate?: string;
  SubTotal: number;
  Tax: number;
  Total: number;
  CreatedAt: Date;
  UpdatedAt: Date;
  Vehicles: InvoiceBoardVehicleDto[];
}

export interface InvoiceStatusResponseDto {
  InvoiceStatusId: string;
  Status: string;
  CreatedAt: Date;
  UpdatedAt: Date;
}

export interface ImageTypeResponseDto {
  ImageTypeId: string;
  ImageType: string;
  CreatedAt: Date;
  UpdatedAt: Date;
}

export interface WorkItemImageResponseDto {
  ImageId: string;
  WorkItemId: string;
  ImageTypeId: string;
  // Signed URL, usable directly in an img src.
  ImageUrl: string;
  CreatedAt: Date;
  UpdatedAt: Date;
}

export interface WorkItemStatusResponseDto {
  WorkItemStatusId: string;
  Status: string;
  CreatedAt: Date;
  UpdatedAt: Date;
}

export interface CreateInvoiceVehicleDto {
  InvoiceId: string;
  VehicleId: string;
}

export interface InvoiceVehicleResponseDto {
  InvoiceVehicleId: string;
  InvoiceId: string;
  VehicleId: string;
  CreatedAt: Date;
  UpdatedAt: Date;
}

export interface CreateWorkItemDto {
  InvoiceVehicleId: string;
  WorkItemStatusId: string;
  EmpUserId?: string;
  Title: string;
  Miles?: number;
  ShopHours?: number;
  PartsPricing?: number;
  FlatFee?: number;
  FlatFeeTaxable?: boolean;
  StartDate?: string;
  EndDate?: string;
  Notes?: string;
}

export interface WorkItemResponseDto {
  WorkItemId: string;
  InvoiceVehicleId: string;
  WorkItemStatusId: string;
  EmpUserId?: string;
  Title: string;
  Miles?: number;
  ShopHours?: number;
  PartsPricing?: number;
  FlatFee?: number | null;
  FlatFeeTaxable?: boolean;
  StartDate?: Date;
  EndDate?: Date;
  Notes?: string;
  CreatedAt: Date;
  UpdatedAt: Date;
}

// A work item (line) within a completed invoice. LineTotal is server-computed.
export interface VehicleServiceHistoryItemDto {
  WorkItemId: string;
  Title: string;
  Miles?: number;
  ShopHours?: number;
  LineTotal: number;
  EmpFirstName?: string;
  EmpLastName?: string;
}

// A vehicle's service history grouped by completed invoice (parent invoice is
// COMPLETE), each carrying its work items.
export interface VehicleServiceHistoryDto {
  InvoiceId: string;
  InvoiceNumber: number;
  CompletedDate?: Date;
  InvoiceTotal: number;
  // False for a previous owner's invoice after the vehicle was transferred: the
  // work history follows the car, but the invoice stays with whoever was billed.
  CanViewInvoice: boolean;
  WorkItems: VehicleServiceHistoryItemDto[];
}

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {
  private apiUrl = `${environment.apiUrl}/invoices`;
  private invoiceStatusApiUrl = `${environment.apiUrl}/invoice-statuses`;
  private invoiceVehicleApiUrl = `${environment.apiUrl}/invoice-vehicles`;
  private workItemApiUrl = `${environment.apiUrl}/work-items`;
  private workItemStatusApiUrl = `${environment.apiUrl}/work-item-statuses`;
  private workItemImageApiUrl = `${environment.apiUrl}/work-item-images`;
  private imageTypeApiUrl = `${environment.apiUrl}/image-types`;

  private pricingConfig$: Observable<PricingConfigDto> | undefined;

  constructor(private http: HttpClient) {}

  /** Pricing config (tax rate) — fetched once per session, replayed to later subscribers. */
  getPricingConfig(): Observable<PricingConfigDto> {
    if (!this.pricingConfig$) {
      this.pricingConfig$ = this.http.get<PricingConfigDto>(`${this.apiUrl}/pricing-config`).pipe(
        shareReplay({ bufferSize: 1, refCount: false }),
        catchError(error => {
          // Drop the failed observable so a later call can retry.
          this.pricingConfig$ = undefined;
          return throwError(() => error);
        })
      );
    }
    return this.pricingConfig$;
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = error.error?.message || `Error Code: ${error.status}\nMessage: ${error.message}`;
    }

    console.error('Invoice Service Error:', errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  /**
   * Get all invoices (requires VIEW_ALL_INVOICES)
   */
  getAllInvoices(): Observable<InvoiceResponseDto[]> {
    return this.http.get<InvoiceResponseDto[]>(
      this.apiUrl
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get all invoices for a given customer user
   */
  getInvoicesByUserId(userId: string): Observable<InvoiceResponseDto[]> {
    return this.http.get<InvoiceResponseDto[]>(
      `${this.apiUrl}/user/${userId}`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get a single invoice by ID
   */
  getInvoiceById(invoiceId: string): Observable<InvoiceResponseDto> {
    return this.http.get<InvoiceResponseDto>(
      `${this.apiUrl}/${invoiceId}`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get the invoice board: all visible invoices hydrated with status name,
   * customer, vehicles, and work items
   */
  getInvoiceBoard(): Observable<InvoiceBoardResponseDto[]> {
    return this.http.get<InvoiceBoardResponseDto[]>(
      `${this.apiUrl}/board`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Update an invoice (e.g. move it to a new status)
   */
  updateInvoice(invoiceId: string, updateData: UpdateInvoiceDto): Observable<InvoiceResponseDto> {
    return this.http.patch<InvoiceResponseDto>(
      `${this.apiUrl}/${invoiceId}`,
      updateData
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Update a work item (status move, reassignment, or detail edits)
   */
  updateWorkItem(workItemId: string, updateData: UpdateWorkItemDto): Observable<WorkItemResponseDto> {
    return this.http.patch<WorkItemResponseDto>(
      `${this.workItemApiUrl}/${workItemId}`,
      updateData
    ).pipe(
      catchError(this.handleError)
    );
  }

  // ============= WORK ITEM IMAGES =============

  /**
   * Get all image types (part-receipt / vehicle-image / other)
   */
  getImageTypes(): Observable<ImageTypeResponseDto[]> {
    return this.http.get<ImageTypeResponseDto[]>(
      this.imageTypeApiUrl
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get all images for a work item (with signed URLs)
   */
  getWorkItemImages(workItemId: string): Observable<WorkItemImageResponseDto[]> {
    return this.http.get<WorkItemImageResponseDto[]>(
      `${this.workItemImageApiUrl}/work-item/${workItemId}`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get a vehicle's completed work items (service history), most recent first.
   */
  getCompletedWorkItemsByVehicle(vehicleId: string): Observable<VehicleServiceHistoryDto[]> {
    return this.http.get<VehicleServiceHistoryDto[]>(
      `${this.workItemApiUrl}/vehicle/${vehicleId}`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get a signed URL for a completed invoice's PDF.
   */
  getInvoicePdfUrl(invoiceId: string): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(
      `${this.apiUrl}/${invoiceId}/pdf-url`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Upload an image for a work item with its image type
   */
  uploadWorkItemImage(workItemId: string, imageFile: File, imageTypeId: string): Observable<WorkItemImageResponseDto> {
    const formData = new FormData();
    formData.append('image', imageFile, imageFile.name);
    formData.append('imageTypeId', imageTypeId);

    return this.http.post<WorkItemImageResponseDto>(
      `${this.workItemImageApiUrl}/upload/${workItemId}`,
      formData
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Update a work item image's metadata (e.g. change its image type)
   */
  updateWorkItemImage(imageId: string, imageTypeId: string): Observable<WorkItemImageResponseDto> {
    return this.http.patch<WorkItemImageResponseDto>(
      `${this.workItemImageApiUrl}/${imageId}`,
      { ImageTypeId: imageTypeId }
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Delete a work item image (storage + database)
   */
  deleteWorkItemImage(imageId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.workItemImageApiUrl}/${imageId}`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Delete an invoice (soft delete; requires EDIT_ALL_INVOICES)
   */
  deleteInvoice(invoiceId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/${invoiceId}`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Delete a work item (soft delete; removes it from the invoice)
   */
  deleteWorkItem(workItemId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.workItemApiUrl}/${workItemId}`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Create a new invoice (or quote)
   */
  createInvoice(invoiceData: CreateInvoiceDto): Observable<InvoiceResponseDto> {
    return this.http.post<InvoiceResponseDto>(
      this.apiUrl,
      invoiceData
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get all invoice statuses (lookup values, e.g. TODO / MECHANIC_REVIEW)
   */
  getInvoiceStatuses(): Observable<InvoiceStatusResponseDto[]> {
    return this.http.get<InvoiceStatusResponseDto[]>(
      this.invoiceStatusApiUrl
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get all work item statuses (lookup values, e.g. TODO / DOING / DONE)
   */
  getWorkItemStatuses(): Observable<WorkItemStatusResponseDto[]> {
    return this.http.get<WorkItemStatusResponseDto[]>(
      this.workItemStatusApiUrl
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Add a vehicle to an invoice (creates the connecting invoiceVehicle row)
   */
  addVehicleToInvoice(invoiceVehicleData: CreateInvoiceVehicleDto): Observable<InvoiceVehicleResponseDto> {
    return this.http.post<InvoiceVehicleResponseDto>(
      this.invoiceVehicleApiUrl,
      invoiceVehicleData
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Create a work item on an invoice vehicle
   */
  createWorkItem(workItemData: CreateWorkItemDto): Observable<WorkItemResponseDto> {
    return this.http.post<WorkItemResponseDto>(
      this.workItemApiUrl,
      workItemData
    ).pipe(
      catchError(this.handleError)
    );
  }
}
